import React, { useEffect, useMemo } from 'react';
import {
  JWT_AUTH_KEY,
  useAuthenticationContext,
  useGetUserInfo,
  USER_INFO_LOCAL_STORAGE_KEY,
  useToast,
} from '@xylexa/xylexa-app';

import secureLocalStorage from 'react-secure-storage';
import mixpanel from 'mixpanel-browser';
import App from './App';
import { useKeycloak } from '@react-keycloak/web';
import { LoadingIndicatorProgress } from '@ohif/ui-next';

mixpanel.init(process.env.MIXPANEL_TOKEN || '8241e183878906c05ee355792c1eb8c5', {
  debug: true,
  track_pageview: false,
  persistence: 'localStorage',
  autocapture: false,
});

export const XylexaLoginPage = props => {
  const { showToast } = useToast();
  const { authToken, currentServerConfigs, setCurrentServerConfigs, setAuthToken, setUserInfo } =
    useAuthenticationContext();

  const { keycloak, initialized } = useKeycloak();

  useEffect(() => {
    if (initialized && !keycloak.authenticated) {
      keycloak.login(); // redirect to login page
    }
  }, [initialized, keycloak.authenticated]);

  useEffect(() => {
    if (keycloak.authenticated && keycloak.token) {
      setAuthToken(keycloak.token);
      secureLocalStorage.setItem(JWT_AUTH_KEY, keycloak.token);
    }
  }, [keycloak.authenticated, keycloak.token]);

  const { data: userInfo, status: userInfoStatus } = useGetUserInfo();

  useEffect(() => {
    if (userInfoStatus === 'success' && userInfo) {
      setUserInfo(userInfo);

      const cloudConfig = userInfo.groupConfig?.find(({ PACS_type }) => PACS_type === 'cloud');
      const localConfig = userInfo.groupConfig?.find(({ PACS_type }) => PACS_type === 'local');
      const serverConfig = localConfig || cloudConfig;

      if (serverConfig) {
        // Prevent redundant updates if config is already set
        const isSameConfig =
          currentServerConfigs &&
          currentServerConfigs.PACS_type === serverConfig.PACS_type &&
          currentServerConfigs.name === serverConfig.name;

        if (!isSameConfig) {
          setCurrentServerConfigs({
            ...serverConfig,
            requestOptions: {
              auth: authToken,
              requestFromBrowser: true,
            },
          });

          showToast({
            content: `Connected to ${serverConfig.PACS_type}`,
            type: 'success',
          });
        }
      }

      secureLocalStorage.setItem(USER_INFO_LOCAL_STORAGE_KEY, userInfo);
    }
  }, [userInfoStatus, userInfo]);

  const config = useMemo(() => {
    if (!currentServerConfigs) return null;

    return {
      ...window.config,
      dataSources: [
        {
          ...window.config?.dataSources?.[0],
          configuration: {
            ...currentServerConfigs,
            // Orthanc supports /rendered for video; this ensures getDirectURL
            // returns a plain URL string instead of a Promise (avoids structuredClone error)
            singlepart: currentServerConfigs.singlepart || 'bulkdata,video',
            bulkDataURI: currentServerConfigs.bulkDataURI ?? { enabled: true },
            omitQuotationForMultipartRequest: currentServerConfigs.omitQuotationForMultipartRequest ?? true,
          },
        },
      ],
    };
  }, [currentServerConfigs]);

  if (!initialized) return <LoadingIndicatorProgress className="h-full w-full" />;

  return (
    <div>
      {keycloak.authenticated && authToken && currentServerConfigs && (
        <App
          config={config}
          defaultExtensions={props.defaultExtensions}
          defaultModes={props.defaultModes}
        />
      )}
    </div>
  );
};
